import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PlayerProvider } from './contexts/PlayerContext';
import { CreditsProvider } from './contexts/CreditsContext';
import BuyCreditsModal from './components/BuyCreditsModal';
import usePresence from './hooks/usePresence';
import { RadioProvider } from './contexts/RadioContext';
import { CartProvider } from './contexts/CartContext';
import { MusicCartProvider } from './contexts/MusicCartContext';
import { BeatsCartProvider } from './contexts/BeatsCartContext';

import Navbar from './components/Navbar';
import FloatingCartFab from './components/FloatingCartFab';
import MiniPlayer from './components/MiniPlayer';
import RadioBar from './components/RadioBar';
import CartDrawer from './components/CartDrawer';
import MusicCartDrawer from './components/MusicCartDrawer';
import BeatsCartDrawer from './components/BeatsCartDrawer';
import AuthCallback from './components/AuthCallback';

import HomePage from './pages/HomePage';
import MusicPage from './pages/MusicPage';
import AlbumsPage from './pages/AlbumsPage';
import BeatsPage from './pages/BeatsPage';
import BulletinPage from './pages/BulletinPage';
import StorePage from './pages/StorePage';
import VideosPage from './pages/VideosPage';
import DownloadsPage from './pages/DownloadsPage';
import LoginPage from './pages/LoginPage';
import AccountPage from './pages/AccountPage';
import AdminPage from './pages/AdminPage';
import MusicPurchaseSuccessPage from './pages/MusicPurchaseSuccessPage';
import BeatsPurchaseSuccessPage from './pages/BeatsPurchaseSuccessPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import BookPurchaseSuccessPage from './pages/BookPurchaseSuccessPage';
import AdvertisePage from './pages/AdvertisePage';
import AdPaymentSuccessPage from './pages/AdPaymentSuccessPage';
import ContactPage from './pages/ContactPage';
import TopBannerAd from './components/ads/TopBannerAd';

import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AppContent = () => {
  const location = useLocation();
  // Anonymous presence heartbeat (powers the live viewer badge)
  usePresence();
  
  // Check for OAuth callback synchronously during render (prevents race conditions)
  if (location.pathname === '/auth/callback' || location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <div className="App min-h-screen bg-[#0D0D0D]">
      <TopBannerAd />
      <RadioBar />
      <Navbar />
      <FloatingCartFab />
      <CartDrawer />
      <MusicCartDrawer />
      <BeatsCartDrawer />
      <main className="pb-20 pt-[6.75rem]">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/music" element={<MusicPage />} />
          <Route path="/music/purchase-success" element={<MusicPurchaseSuccessPage />} />
          <Route path="/albums" element={<AlbumsPage />} />
          <Route path="/albums/:slug" element={<AlbumsPage />} />
          <Route path="/beats" element={<BeatsPage />} />
          <Route path="/beats/purchase-success" element={<BeatsPurchaseSuccessPage />} />
          <Route path="/bulletin" element={<BulletinPage />} />
          <Route path="/books/purchase-success" element={<BookPurchaseSuccessPage />} />
          <Route path="/store" element={<StorePage />} />
          <Route path="/videos" element={<VideosPage />} />
          <Route path="/downloads" element={<DownloadsPage />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
          <Route path="/advertise" element={<AdvertisePage />} />
          <Route path="/advertise/payment-success" element={<AdPaymentSuccessPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <AccountPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <MiniPlayer />
      <BuyCreditsModal />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CreditsProvider>
          <PlayerProvider>
            <RadioProvider>
              <CartProvider>
                <MusicCartProvider>
                  <BeatsCartProvider>
                    <AppContent />
                  </BeatsCartProvider>
                </MusicCartProvider>
              </CartProvider>
            </RadioProvider>
          </PlayerProvider>
        </CreditsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
